export function SellApartmentsWithTenants() {
  return (
    <section className="py-20 bg-gray-50" id="sell-with-tenants">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sell Apartments with Tenants - Complete Guide
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Selling an occupied apartment building requires special considerations. Learn how to navigate tenant rights, 
            lease agreements, and buyer expectations while protecting everyone's interests.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/100fd0457cf9ac937ce1100519495fd3.jpg"
              alt="Tenants in apartment building during property transition"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Tenant Rights and Legal Considerations</h3>
            <p className="text-gray-600 mb-6">
              When selling apartments with existing tenants, both state and local laws protect tenant rights during ownership 
              transitions. Understanding these requirements is crucial for a smooth sale.
            </p>
            
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <i className="ri-file-text-line text-orange-500 mr-2"></i>
                  Lease Agreement Transfers
                </h4>
                <p className="text-gray-600">
                  All existing leases automatically transfer to the new owner. Tenants cannot be forced to move unless 
                  there are lease violations or the new owner plans major renovations.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <i className="ri-notification-line text-orange-500 mr-2"></i>
                  Required Notifications
                </h4>
                <p className="text-gray-600">
                  Most states require 30-90 days written notice to tenants about the pending sale, including new owner 
                  contact information and any changes to lease terms.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <i className="ri-shield-check-line text-orange-500 mr-2"></i>
                  Security Deposit Protection
                </h4>
                <p className="text-gray-600">
                  Security deposits must be properly transferred to the new owner or returned to tenants with appropriate 
                  documentation and accounting.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Steps to Successfully Sell with Tenants
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-list-3-line text-3xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Document Everything</h4>
              <p className="text-gray-600">
                Compile current rent rolls, lease agreements, tenant contact information, and payment histories 
                for potential buyers.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-send-line text-3xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Notify Tenants</h4>
              <p className="text-gray-600">
                Send proper legal notices about the sale, showings, and any changes that may occur during 
                the transition process.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Coordinate Showings</h4>
              <p className="text-gray-600">
                Schedule property viewings with advance notice to tenants, respecting their privacy 
                and quiet enjoyment rights.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-handshake-line text-3xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Smooth Transition</h4>
              <p className="text-gray-600">
                Coordinate with the new owner to ensure seamless transfer of deposits, leases, 
                and ongoing tenant relationships.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-green-50 rounded-xl p-8 border-2 border-green-200">
            <h4 className="text-2xl font-bold text-green-800 mb-6">
              <i className="ri-thumb-up-line mr-3"></i>
              Benefits of Selling with Tenants
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Proven Income Stream</h5>
                  <p className="text-gray-600">Existing tenants demonstrate actual rental income, not just projected returns.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Immediate Cash Flow</h5>
                  <p className="text-gray-600">New owners start receiving rental income from day one of ownership.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Market Validation</h5>
                  <p className="text-gray-600">Occupied units prove market demand and appropriate rental pricing.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Investor Appeal</h5>
                  <p className="text-gray-600">Many investors prefer properties with established tenant bases and lease histories.</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-red-50 rounded-xl p-8 border-2 border-red-200">
            <h4 className="text-2xl font-bold text-red-800 mb-6">
              <i className="ri-alert-line mr-3"></i>
              Challenges to Consider
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-close-line text-white text-sm"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Limited Showing Flexibility</h5>
                  <p className="text-gray-600">Must provide proper notice and work around tenant schedules for property viewings.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-close-line text-white text-sm"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Tenant Relations</h5>
                  <p className="text-gray-600">Poor tenant relationships or problem tenants can negatively impact sale price.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-close-line text-white text-sm"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Below-Market Rents</h5>
                  <p className="text-gray-600">Long-term tenants paying below-market rates may reduce property value.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-close-line text-white text-sm"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Legal Complications</h5>
                  <p className="text-gray-600">Must navigate tenant protection laws and lease transfer requirements.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-12 text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            We Specialize in Occupied Properties
          </h3>
          <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
            We have extensive experience buying apartment buildings with existing tenants. We handle all legal requirements, 
            tenant communications, and lease transfers to ensure a smooth transition for everyone involved.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-gray-700">Properties with Tenants Purchased</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl font-bold text-blue-600 mb-2">98%</div>
              <div className="text-gray-700">Tenant Retention Rate</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl font-bold text-blue-600 mb-2">15 Days</div>
              <div className="text-gray-700">Average Closing Time</div>
            </div>
          </div>
          <button className="bg-orange-500 hover:bg-orange-600 text-white px-12 py-4 rounded-lg font-semibold text-lg whitespace-nowrap">
            Get Offer for Occupied Property
          </button>
        </div>
      </div>
    </section>
  );
}